import { useEffect, useState } from "react";
import { client } from "../lib/config";
import { Star, Check } from "lucide-react";

// Format "YYYY-MM-DD" as "Month YYYY".
function formatReviewDate(d) {
  if (!d) return "";
  const [y, m] = d.split("-");
  const yr = Number(y);
  const mo = Number(m) - 1;
  if (Number.isNaN(yr) || Number.isNaN(mo) || mo < 0 || mo > 11) return "";
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${MONTHS[mo]} ${yr}`;
}

export default function ReviewsStrip({ compact = false }) {
  const [payload, setPayload] = useState({ source: "curated", reviews: [] });
  useEffect(() => {
    client.get("/reviews").then((r) => setPayload(r.data)).catch(() => {});
  }, []);
  const items = payload.reviews || [];
  if (!items.length) return null;
  const isLive = payload.source === "google";
  const show = compact ? items.slice(0, 2) : items.slice(0, 3);
  return (
    <section className="bg-[#F5F0E6] border-y border-[#E5DFD3]" data-testid="reviews-strip">
      <div className="wb-container py-14 md:py-16">
        <div className="max-w-2xl mb-8 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="wb-eyebrow">What guests say</div>
            <h3 className="wb-h3 mt-3">Real feedback from real folks who slept here.</h3>
          </div>
          {isLive && payload.rating != null && (
            <div className="text-right text-sm text-[#1A1A1A]" data-testid="reviews-strip-aggregate">
              <div className="flex items-center gap-1 justify-end">
                <Star className="h-4 w-4 fill-[#E85D26] text-[#B94A15]" aria-hidden="true" />
                <span className="font-black text-lg" style={{ fontFamily: "'Nunito', sans-serif" }}>
                  {Number(payload.rating).toFixed(1)}
                </span>
                <span className="text-[#4A4641]">/ 5 · {payload.review_count} reviews</span>
              </div>
              <div className="text-xs text-[#4A4641] mt-1">via Google</div>
            </div>
          )}
        </div>
        <div className={`grid grid-cols-1 ${compact ? "md:grid-cols-2" : "md:grid-cols-3"} gap-6`}>
          {show.map((t, i) => (
            <figure key={t.id || i} className="wb-card p-6 flex flex-col" data-testid={`reviews-strip-item-${i}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex gap-0.5" aria-label={`${t.rating || 5} out of 5 stars`}>
                  {Array.from({ length: t.rating || 5 }).map((_, k) => (
                    <Star key={k} className="h-4 w-4 fill-[#E85D26] text-[#B94A15]" aria-hidden="true" />
                  ))}
                </div>
                {!isLive && t.verified && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.14em] text-[#B94A15] bg-[#FCEEEA] border border-[#F5B98D] px-2 py-1 rounded-sm"
                    style={{ fontFamily: "'Nunito', sans-serif" }}
                    data-testid={`reviews-strip-verified-${i}`}
                  >
                    <Check className="h-3 w-3" aria-hidden="true" /> Verified
                  </span>
                )}
              </div>
              <blockquote className="text-[#1A1A1A] text-sm flex-1">
                <span className="text-2xl text-[#B94A15] font-black leading-none" aria-hidden="true">"</span>
                {isLive ? t.text : t.quote}
              </blockquote>
              <figcaption className="mt-4 pt-3 border-t border-[#E5DFD3]">
                <div className="font-bold text-[#1A1A1A] text-sm" style={{ fontFamily: "'Nunito', sans-serif" }}>
                  {isLive ? t.author : t.name}
                </div>
                <div className="text-xs text-[#4A4641] mt-0.5">
                  {isLive ? (
                    <>{t.relative_time || ""} · <span>via Google</span></>
                  ) : (
                    <>
                      {formatReviewDate(t.review_date)}
                      {t.review_date && t.source_platform ? " · " : ""}
                      {t.source_platform && (
                        <span>via <span className="font-bold text-[#1A1A1A]">{t.source_platform}</span></span>
                      )}
                    </>
                  )}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
